<script lang="tsx">
import { renderHtmlFragment } from '#shared'
import { capitalize, computed, defineComponent, h, toRefs, useCssModule } from 'vue'
import { parseBbCode } from '#features/bb-code'
import { checkIsEmoji } from '#features/emoji-selection'
import type { Message } from '#entities/message'
import { insertMessageLinks } from '../lib/insert-message-links'
import { insertMessagePings } from '../lib/insert-message-pings'
import Link from './link.vue'

interface MessageListItemTextProps {
  message: Message
  variant?: 'regular' | 'compact'
}

export default defineComponent<MessageListItemTextProps>({
  props: {
    message: {
      type: Object,
    },
    variant: {
      type: String,
      default: 'regular',
    },
  },

  setup(props) {
    const { message, variant } = toRefs(props)

    const $style = useCssModule()
    const isLarge = computed(() => (message.value?.text?.length ?? 0) < 12 && checkIsEmoji(message.value?.text ?? ''))

    const parsedBbCode = computed(() => {
      if (!message.value?.isBBCode) return ''
      const parsedBbCode = parseBbCode(message.value.text ?? '')
      return parsedBbCode
    })

    const messageContent = computed(() => {
      let messageContent = message.value?.isBBCode ? parsedBbCode.value : message.value?.text || ''
      messageContent = insertMessagePings(messageContent, message.value?.pings ?? {})
      messageContent = insertMessageLinks(messageContent)
      return messageContent
    })

    return () =>
      h(
        'div',
        {
          class: [
            $style.text,
            $style['textVariant' + capitalize(variant.value!)],
            { [$style['textLargeVariant' + capitalize(variant.value!)]]: isLarge.value },
            { [$style.textBbCode]: message.value?.isBBCode },
          ],
        },
        renderHtmlFragment(messageContent.value, { a: Link })
      )
  },
})
</script>

<style lang="scss" module>
@use '#mixins/media' as media;

.text {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'HelveticaNeueSbbChat', 'Noto Color Emoji', system-ui, Avenir, Helvetica, Arial, sans-serif;
}

.textVariantRegular {
  font-size: 16px;

  @include media.mobile {
    font-size: 14px;
  }
}

.textVariantCompact {
  font-size: 14px;
}

.textLargeVariantRegular {
  font-size: 32px;

  @include media.mobile {
    font-size: 24px;
  }
}

.textLargeVariantCompact {
  font-size: 24px;
}

.textBbCode {
  img {
    max-height: 200px;
    max-width: 200px;
  }

  blockquote {
    padding-left: 10px;
    border-left: 2px solid #4a8dff60;
  }

  table {
    border: 1px solid #474756;
    width: 100%;
  }

  tr:not(:last-child) {
    border-bottom: 1px solid #474756;
  }

  td:not(:last-child) {
    border-right: 1px solid #474756;
  }

  ul {
    list-style: circle;
  }

  ol {
    list-style: decimal;
    padding-left: 1.5em;
  }

  summary {
    cursor: pointer;
  }
}
</style>
